import { Eye, FileText, Search, Settings } from 'lucide-react';
import React, { useState } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import PdfTemplateEditor from '../components/pdf-templates/PdfTemplateEditor';
import { useGetPdfTemplatesQuery } from '../services/pdfTemplateApi';
import type { PdfTemplate } from '../types/pdfTemplate';

const moduleDisplayNames: Record<string, string> = {
  'purchase-orders': 'Purchase Orders',
  'change-orders': 'Change Orders',
  'daily-logs': 'Daily Logs',
  rfis: 'RFIs',
  'client-invoices': 'Payment Applications',
  invoicing: 'Invoices',
  'field-reports': 'Field Reports',
  compliance: 'Compliance',
  deliveries: 'Deliveries',
  'bid-requests': 'Bid Requests',
  equipment: 'Equipment',
  'quality-reports': 'Quality Reports',
  proposals: 'Proposals',
  estimates: 'Estimates',
  contracts: 'Contracts',
  timesheets: 'Timesheets',
  meetings: 'Meetings',
  'safety-reports': 'Safety Reports',
  bills: 'Bills',
  submittals: 'Submittals',
  takeoff: 'Takeoffs',
};

const PdfTemplates: React.FC = () => {
  const { data: response, isLoading } = useGetPdfTemplatesQuery();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<PdfTemplate | null>(null);

  const templates = response?.data || [];

  const filteredTemplates = templates.filter(
    t =>
      t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.moduleType.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-brand-50 rounded-lg flex items-center justify-center">
            <FileText className="h-5 w-5 text-brand-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">PDF Templates</h1>
            <p className="text-sm text-gray-500">
              Customize PDF output for all document types across your organization
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search templates..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
        </div>
      </div>

      {/* Template Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredTemplates.map(template => (
          <div
            key={template._id}
            className="bg-white border border-gray-200 rounded-xl p-5 hover:border-brand-300 hover:shadow-sm transition-all cursor-pointer group"
            onClick={() => setSelectedTemplate(template)}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-gray-400 group-hover:text-brand-500 transition-colors" />
                <h3 className="font-medium text-gray-900 text-sm">{template.name}</h3>
              </div>
              <div className="flex items-center space-x-1">
                {template.isSystem && !template.organizationId && (
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                    System
                  </span>
                )}
                {template.organizationId && (
                  <span className="text-xs bg-brand-50 text-brand-600 px-2 py-0.5 rounded-full">
                    Customized
                  </span>
                )}
                <span
                  className={`w-2 h-2 rounded-full ${template.isActive ? 'bg-green-500' : 'bg-gray-300'}`}
                />
              </div>
            </div>

            <p className="text-xs text-gray-500 mb-3 line-clamp-2">
              {template.description ||
                `PDF template for ${moduleDisplayNames[template.moduleType] || template.moduleType}`}
            </p>

            <div className="flex items-center justify-between text-xs text-gray-400">
              <span>{moduleDisplayNames[template.moduleType] || template.moduleType}</span>
              <span>v{template.version}</span>
            </div>

            {/* Hover actions */}
            <div className="mt-3 pt-3 border-t border-gray-100 flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                className="flex items-center space-x-1 text-xs text-gray-600 hover:text-brand-600"
                onClick={e => {
                  e.stopPropagation();
                  setSelectedTemplate(template);
                }}
              >
                <Settings className="h-3.5 w-3.5" />
                <span>Configure</span>
              </button>
              <button className="flex items-center space-x-1 text-xs text-gray-600 hover:text-brand-600">
                <Eye className="h-3.5 w-3.5" />
                <span>Preview</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredTemplates.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-sm font-medium text-gray-900">No templates found</h3>
          <p className="text-sm text-gray-500 mt-1">
            {searchTerm
              ? 'Try a different search term'
              : 'PDF templates will appear here once seeded'}
          </p>
        </div>
      )}

      {/* Editor Drawer */}
      {selectedTemplate && (
        <PdfTemplateEditor
          template={selectedTemplate}
          isOpen={!!selectedTemplate}
          onClose={() => setSelectedTemplate(null)}
        />
      )}
    </div>
  );
};

export default PdfTemplates;
